import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faBellSlash, faCheck, faChevronDown, faChevronLeft, faChevronRight, faEye, faRotateLeft, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import SidePanel from "../ui/side-panel";
import Icon3D from "../ui/icon-3d";
import StatusPill from "../ui/status-pill";
import SkeletonList from "../ui/skeleton-list";
import { LatencyBadge } from "../ui/latency";
import { Fields, PanelTitle } from "../ui/detail";
import TimeAgo from "../time-ago";
import LogDetail from "./log-detail";
import PolicyChip from "./policy-chip";
import { AlertAction, ApiLog, ApiLogDetail, LogGroup, PAYMENT_DECLINE_TYPE, fetchLog, fetchLogs, policyOf, updateAlert } from "../../utils/api";
import { CATEGORY_LOOK, HEALTH_LOOK, platformLabel } from "../../utils/status";
import { formatDateTime } from "../../utils/format-time";
import { ParseErrors } from "../../utils/ParseErrors";

const ACTIONS: Record<AlertAction, { label: string; done: string; icon: IconDefinition }> = {
  acknowledge: { label: "Acknowledge", done: "Acknowledged", icon: faEye },
  mute: { label: "Mute", done: "Muted · it won't show in Alerts", icon: faBellSlash },
  resolve: { label: "Resolve", done: "Resolved", icon: faCheck },
  reopen: { label: "Unmute / reopen", done: "Reopened", icon: faRotateLeft },
};

const EXPAND_ALL = 20;

const actionsFor = (state: LogGroup["state"]): AlertAction[] =>
  state === "open" ? ["acknowledge", "mute", "resolve"] : state === "acknowledged" ? ["mute", "resolve"] : ["reopen"];

// ----------------------- ONE INCIDENT: SUMMARY, ACTIONS, EVERY OCCURRENCE ------------------------------------
export default function GroupPanel({ group, onClose, onChanged }: { group: LogGroup | null; onClose: () => void; onChanged: () => void }) {
  const [occurrences, setOccurrences] = useState<ApiLog[] | null>(null);
  const [total, setTotal] = useState(0);
  const [details, setDetails] = useState<Record<number, ApiLogDetail>>({});
  const [expanded, setExpanded] = useState<number[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!group) return;
    try {
      const result = await fetchLogs({ fingerprint: group.fingerprint, platform: group.platform, statusCode: group.statusCode, environment: group.environment ?? "production", limit: 50 });
      setOccurrences(result.data);
      setTotal(result.totalCount);
    } catch (error) {
      toast.error(ParseErrors(error));
    }
  }, [group]);

  useEffect(() => {
    setOccurrences(null);
    setDetails({});
    setExpanded([]);
    setSelectedIndex(null);
    void load();
  }, [load]);

  // ----------------------- LOAD DETAILS ONCE, KEEP THEM FOR PREV / NEXT AND THE ACCORDION ------------------------------------
  const loadDetails = async (ids: number[]) => {
    const missing = ids.filter((id) => !details[id]);
    if (!missing.length) return true;
    try {
      const loaded = await Promise.all(missing.map(fetchLog));
      setDetails((current) => ({ ...current, ...Object.fromEntries(loaded.map((log) => [log.id, log])) }));
      return true;
    } catch (error) {
      toast.error(ParseErrors(error));
      return false;
    }
  };

  const toggle = async (id: number) => {
    if (expanded.includes(id)) return setExpanded(expanded.filter((item) => item !== id));
    if (await loadDetails([id])) setExpanded([...expanded, id]);
  };

  const expandAll = async () => {
    const ids = (occurrences ?? []).slice(0, EXPAND_ALL).map((log) => log.id);
    if (await loadDetails(ids)) setExpanded(ids);
  };

  const openFull = async (index: number) => {
    const id = occurrences?.[index]?.id;
    if (id && (await loadDetails([id]))) setSelectedIndex(index);
  };

  const selectedLog = selectedIndex != null ? details[occurrences?.[selectedIndex]?.id ?? -1] : null;

  const act = async (action: AlertAction) => {
    if (!group) return;
    setBusy(true);
    try {
      await updateAlert(`api:${group.id}`, action);
      toast.success(ACTIONS[action].done);
      onChanged();
      onClose();
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setBusy(false);
    }
  };

  const decline = group?.type === PAYMENT_DECLINE_TYPE;
  const look = decline ? CATEGORY_LOOK.payment : CATEGORY_LOOK[group?.routeCategory ?? "general"] ?? CATEGORY_LOOK.general;
  const route = group?.route.replace(/^route:/, "") ?? "";

  return (
    <SidePanel
      open={!!group}
      onClose={onClose}
      icon={group ? <Icon3D icon={look.icon} tone={decline ? "amber" : HEALTH_LOOK[group.status].tone} size="lg" /> : null}
      title={<span className="font-mono text-[15px]">{route}</span>}
      subtitle={group ? `${platformLabel(group.platform)} · ${decline ? "Payment decline" : look.label} · ${group.environment ?? "production"}` : undefined}
      badge={
        group ? (
          <>
            <StatusPill tone="indigo">{group.count.toLocaleString()} times</StatusPill>
            <StatusPill tone={decline ? "amber" : HEALTH_LOOK[group.status].tone} live={!decline && group.state === "open" && group.status === "critical"}>{group.statusCode}</StatusPill>
            {group.state !== "open" ? <StatusPill tone="slate">{group.state}</StatusPill> : null}
          </>
        ) : null
      }
      footer={
        group && !selectedLog
          ? actionsFor(group.state).map((action) => (
              <button key={action} type="button" disabled={busy} onClick={() => act(action)} className="inline-flex h-10 items-center gap-2 rounded-xl border border-edge bg-surface px-3.5 font-[Gilroy-Medium] text-[13px] text-light-gray transition-colors hover:border-accent hover:text-heading-color disabled:opacity-60">
                <FontAwesomeIcon icon={ACTIONS[action].icon} className="text-[11px]" />
                {ACTIONS[action].label}
              </button>
            ))
          : null
      }
    >
      {!group ? null : selectedLog && selectedIndex != null ? (
        <>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setSelectedIndex(null)} className="flex items-center gap-2 font-[Gilroy-Medium] text-[13px] text-blue hover:underline">
              <FontAwesomeIcon icon={faArrowLeft} className="text-[11px]" />
              All {total.toLocaleString()} occurrences
            </button>
            <span className="ml-auto font-[Gilroy-Medium] text-[12px] text-label-muted">{selectedIndex + 1} of {occurrences?.length ?? 0}</span>
            {[
              { icon: faChevronLeft, label: "Previous", to: selectedIndex - 1 },
              { icon: faChevronRight, label: "Next", to: selectedIndex + 1 },
            ].map((move) => (
              <button key={move.label} type="button" aria-label={move.label} disabled={move.to < 0 || move.to >= (occurrences?.length ?? 0)} onClick={() => openFull(move.to)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-edge text-light-gray transition-colors hover:border-accent hover:text-heading-color disabled:opacity-40">
                <FontAwesomeIcon icon={move.icon} className="text-[11px]" />
              </button>
            ))}
          </div>
          <LogDetail log={selectedLog} />
        </>
      ) : (
        <>
          <p className="font-[Gilroy-Medium] text-[14px] text-heading-color">{group.message}</p>
          {decline ? (
            <p className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-3 font-[Gilroy-Medium] text-[13px] text-amber-700 dark:text-amber-300">
              Customer payment decline (card details or funds). It's logged and grouped here, but never counted as an error or alert. Mute it if you don't need to see it.
            </p>
          ) : null}
          <Fields
            rows={[
              ["Platform", platformLabel(group.platform)],
              ["Status code", group.statusCode],
              ["Times seen", <b>{group.count.toLocaleString()}</b>],
              ["First seen", formatDateTime(group.firstSeen)],
              ["Last seen", <TimeAgo value={group.lastSeen} />],
              ["State", <span className="capitalize">{group.state}</span>],
            ]}
          />
          <div className="flex items-center justify-between gap-2">
            <PanelTitle>Occurrences {total ? `(latest ${Math.min(total, 50)} of ${total.toLocaleString()} rows)` : ""}</PanelTitle>
            {occurrences?.length ? (
              <button type="button" onClick={expanded.length ? () => setExpanded([]) : expandAll} className="shrink-0 font-[Gilroy-Medium] text-[12px] text-blue hover:underline">
                {expanded.length ? "Collapse all" : `Expand all${occurrences.length > EXPAND_ALL ? ` (first ${EXPAND_ALL})` : ""}`}
              </button>
            ) : null}
          </div>
          {!occurrences ? (
            <SkeletonList rows={4} height="h-12" />
          ) : !occurrences.length ? (
            <p className="text-[13px] text-light-gray">No stored rows for this incident in production.</p>
          ) : (
            <ul className="glass divide-y divide-edge overflow-hidden p-0">
              {occurrences.map((log, index) => {
                const open = expanded.includes(log.id);
                return (
                  <li key={log.id}>
                    <div className="flex items-center">
                      <button type="button" aria-expanded={open} onClick={() => toggle(log.id)} className="flex min-w-0 flex-1 items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-overlay">
                        <span className="w-24 shrink-0 font-[Gilroy-Medium] text-[12px] text-light-gray"><TimeAgo value={log.createdAt} /></span>
                        {policyOf(log) ? <PolicyChip value={policyOf(log)} /> : null}
                        <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-label-muted">{log.correlationId ?? `#${log.id}`}</span>
                        {log.count > 1 ? <span className={clsx("rounded-full bg-overlay-mid px-1.5 font-[Gilroy-Bold] text-[11px] text-light-gray")}>×{log.count}</span> : null}
                        <LatencyBadge ms={log.responseTime} />
                        <FontAwesomeIcon icon={faChevronDown} className={clsx("text-[10px] text-label-muted transition-transform duration-300", open && "rotate-180")} />
                      </button>
                      <button type="button" aria-label="Open with previous / next" onClick={() => openFull(index)} className="px-3 py-2.5 text-label-muted transition-colors hover:text-heading-color">
                        <FontAwesomeIcon icon={faUpRightFromSquare} className="text-[11px]" />
                      </button>
                    </div>
                    {open && details[log.id] ? <div className="space-y-3 border-t border-edge bg-overlay/40 p-4"><LogDetail log={details[log.id]} /></div> : null}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </SidePanel>
  );
}
